/**
 * wet_state — THE WET CLOCK (shader gaps program round 3, weather surfaces, 2026-09-30). PURE: no three, no DOM.
 *
 * One clock for everything the weather wets: the ground (the soil class drives `lib/wetness.ts`'s uniforms), the
 * props and structures (`lib/wet_layer.ts`), and — later — the shallow-water puddle patch (brief L reads these rates;
 * it must never keep a second clock). Before it, wetness was ONE number lerped toward the weather's target with a
 * ~1.7 s time constant: a downpour's end dried the whole world in two seconds.
 *
 * After Lagarde, "Water drop 3a/3b — physically based wet surfaces" (2013): a wet surface is two things with two
 * speeds —
 *   • ABSORBED water fills the PORES of a porous material (brick, concrete, plaster, wood, soil, fabric) and darkens
 *     it (the pores' air replaced by water: light scatters forward, total internal reflection at the film traps it —
 *     the darkening is `lerp(1, 0.2, porosity)`, 0.2 the floor of measured wet/dry ratios);
 *   • the SURFACE FILM sits on top (any material — metal and glass carry only this): it smooths the microsurface (the
 *     water's own ~0.05 roughness) and, pooled, is a puddle's level mirror.
 * The film forms first and goes first; the pores fill from the film and dry last.
 *
 * DRYING is evaporation, not a timer. The rate of a wet surface is Carrier's (1918, the ASHRAE handbook's pool
 * correlation, a Dalton law): E = (0.089 + 0.0782·u)·(p_w − p_a)/Y [kg m⁻² s⁻¹ = mm s⁻¹], u the wind (m/s), p_w the
 * saturation vapour pressure at the SURFACE temperature, p_a the air's (RH × p_sat(air)), Y = 2450 kJ/kg. The sun
 * warms the surface (p_w climbs steeply — Magnus/Tetens), the wind thins the boundary layer, humid air slows it
 * (and saturated, cold, still air deposits dew: E < 0). A porous body dries in the drying science's two periods: the
 * CONSTANT-RATE period (capillaries keep the surface wet; E as for free water) until the pores are ~40 % full, then
 * the FALLING-RATE period (the meniscus recedes; the rate falls with the saturation) — the sigmoid tail Lagarde
 * describes ("drying follows a sigmoid curve rather than a linear decay").
 *
 * GAME TIME. The Commons day is 7,200 s (12× faster than a real one): every rate here runs on `timeScale` (default
 * WET_TIME_SCALE = 12), so a concrete wall that takes ~40 min to dry in real sun takes ~3–4 min, a humid overcast
 * afternoon ~15–20 min, a metal film in sun under a minute.
 *
 * FRAME: the clock is per surface CLASS in the open air (exposure 1). A receiver shows `level × its exposure` — the
 * roof map (`rain_shelter`) says how much of that weather reaches it — so a rebase, a streamed-in building or a crate
 * that loads mid-storm reads the same wetness as its neighbours: there is no per-object state to lose.
 */

export type WetClass = 'soil' | 'stone' | 'wood' | 'fabric' | 'metal' | 'glass';
export const WET_CLASSES: readonly WetClass[] = ['soil', 'stone', 'wood', 'fabric', 'metal', 'glass'];

export interface WetClassParams {
  /** Open porosity 0..1 — Lagarde's darkening weight (0 = no absorbed water, no darkening). */
  porosity: number;
  /** Water the surface holds as a film before it runs off (mm). */
  filmCapMm: number;
  /** Water the pores hold at saturation (mm); 0 = non-porous. */
  poreCapMm: number;
  /** Film → pores at the surface (mm/h): how fast the material drinks. */
  infilMmH: number;
  /** Standing water a surface can pond in its hollows (mm) — the soil's puddles; 0 elsewhere. */
  pondCapMm: number;
  /** °C the surface warms above the air under full sun (dark, massive surfaces most). */
  sunGainC: number;
  /** Per GAME hour, the fraction of the skin's water that wicks INWARD (the dry bulk's capillary suction; a soil's
   *  drainage; a fabric's drip) — why a wall lightens faster than evaporation alone would dry it. */
  inwardPerH: number;
}

/**
 * Per class. Film capacities after surface-storage figures (smooth metal/glass ~0.1 mm beaded, rough stone and
 * soil 0.2–0.3 mm); pore capacities are the "skin" that shows (a few mm of a material's top); infiltration is the
 * rate the skin drinks from its film. Tuned so the ORDER of drying matches Lagarde's observations (film first,
 * metal/glass minutes, porous tens of minutes, fabric slowest), not to a lab.
 */
export const WET_PARAMS: Record<WetClass, WetClassParams> = {
  soil: { porosity: 0.6, filmCapMm: 0.3, poreCapMm: 2.0, infilMmH: 6, pondCapMm: 3.0, sunGainC: 14, inwardPerH: 1.0 },
  stone: { porosity: 0.5, filmCapMm: 0.2, poreCapMm: 1.0, infilMmH: 3, pondCapMm: 0, sunGainC: 12, inwardPerH: 0.7 },
  wood: { porosity: 0.45, filmCapMm: 0.2, poreCapMm: 0.8, infilMmH: 2, pondCapMm: 0, sunGainC: 9, inwardPerH: 0.6 },
  fabric: { porosity: 0.85, filmCapMm: 0.05, poreCapMm: 1.6, infilMmH: 24, pondCapMm: 0, sunGainC: 8, inwardPerH: 0.5 },
  metal: { porosity: 0, filmCapMm: 0.12, poreCapMm: 0, infilMmH: 0, pondCapMm: 0, sunGainC: 16, inwardPerH: 0 },
  glass: { porosity: 0, filmCapMm: 0.1, poreCapMm: 0, infilMmH: 0, pondCapMm: 0, sunGainC: 6, inwardPerH: 0 },
};

/** Game seconds per real second of the default Commons day (86,400 / 7,200). */
export const WET_TIME_SCALE = 12;
/** Latent heat of vaporisation (kJ/kg) at ~20 °C — Carrier's Y. */
const LATENT_KJ_KG = 2450;
/** Saturated pores dry at the free-water rate down to this saturation (the constant-rate period's end). */
export const CRITICAL_SATURATION = 0.4;
/** The falling-rate period never stops a surface drying entirely (the vapour still diffuses). */
const FALLING_RATE_FLOOR = 0.08;
/** Dew deposits at most this fast (mm/h, real) — a still, saturated night. */
const DEW_MAX_MMH = 0.04;

export interface WetInputs {
  /** Rain reaching an open, level surface (mm/h). Snow does not wet (it accumulates — the snow layer). */
  rainMmH: number;
  /** Air temperature (°C). */
  tempC: number;
  /** Relative humidity 0..1. */
  rh: number;
  /** Wind at 2 m (m/s). */
  windMs: number;
  /** Direct sun on the surface 0..1 (day × clear sky). */
  sun: number;
  /** °C a surface radiates BELOW the air under a clear night sky (dew forms when it falls under the dew point). */
  cool?: number;
}

/** A surface's water, in mm: the film on top, the water in its pores, the water ponded in its hollows. */
export interface WetState { film: number; pores: number; pond: number }
export function newWetState(): WetState { return { film: 0, pores: 0, pond: 0 }; }

/** Saturation vapour pressure over water (kPa), Magnus–Tetens. */
export function satVapourKPa(tC: number): number { return 0.6108 * Math.exp((17.27 * tC) / (tC + 237.3)); }

/**
 * Evaporation from a wet surface (mm/h, REAL time; negative = dew) — Carrier's correlation with the surface warmed by
 * the sun. `sunGainC` is the class's own (a dark soil in sun is far warmer than the air; glass barely).
 */
export function evaporationMmH(tempC: number, rh: number, windMs: number, sun: number, sunGainC: number, coolC = 0): number {
  const ts = tempC + sunGainC * Math.max(0, Math.min(1, sun)) - Math.max(0, coolC);
  const dp = satVapourKPa(ts) - Math.max(0, Math.min(1, rh)) * satVapourKPa(tempC);
  const e = ((0.089 + 0.0782 * Math.max(0, windMs)) * dp) / LATENT_KJ_KG * 3600; // kg m⁻² s⁻¹ → mm/h
  return Math.max(-DEW_MAX_MMH, e);
}

/** The falling-rate factor of a porous body at saturation `sat` (1 in the constant-rate period). */
export function dryingFactor(sat: number): number {
  if (sat >= CRITICAL_SATURATION) return 1;
  return Math.max(FALLING_RATE_FLOOR, sat / CRITICAL_SATURATION);
}

/**
 * Advance one surface by `dtS` REAL seconds (× timeScale game time). The water budget, per game hour:
 *   film  += rain − evaporation − infiltration;  overflow past the film cap runs off (soil: into the pond)
 *   pores += infiltration (capped by the pore space left); dries only once the film is gone, at E × dryingFactor,
 *            and wicks inward (× e^(−inwardPerH·h)) while nothing feeds it
 *   pond  += overflow; drains by infiltration (a third of the skin's rate) and evaporation
 * Returns the same object (mutated).
 */
export function stepWet(s: WetState, cls: WetClass, inp: WetInputs, dtS: number, timeScale = WET_TIME_SCALE): WetState {
  const P = WET_PARAMS[cls];
  const h = (Math.max(0, dtS) * timeScale) / 3600;
  if (!(h > 0)) return s;
  const e = evaporationMmH(inp.tempC, inp.rh, inp.windMs, inp.sun, P.sunGainC, inp.cool ?? 0);
  const rain = Math.max(0, inp.rainMmH);
  // film: in from the rain (or dew), out by evaporation and into the pores
  const poreRoom = P.poreCapMm > 0 ? Math.max(0, 1 - s.pores / P.poreCapMm) : 0;
  const infil = Math.min(P.infilMmH * poreRoom * h, s.film + rain * h);
  let film = s.film + rain * h - infil - e * h;
  let pores = s.pores + infil;
  let pond = s.pond;
  if (film > P.filmCapMm) {
    const over = film - P.filmCapMm;
    film = P.filmCapMm;
    if (P.pondCapMm > 0) pond = Math.min(P.pondCapMm, pond + over);
  }
  if (film < 0) {
    // the film is gone: the evaporation it could not supply takes from the pores (at the drying curve's rate)
    const deficit = -film;
    film = 0;
    if (P.poreCapMm > 0) pores = Math.max(0, pores - deficit * dryingFactor(pores / P.poreCapMm));
  }
  // the skin's water wicks into the bulk while no film feeds it (exponential: a saturated skin lightens first fast)
  if (P.inwardPerH > 0 && film <= 0 && rain <= 0) pores *= Math.exp(-P.inwardPerH * h);
  if (P.pondCapMm > 0 && pond > 0) {
    const drain = (P.infilMmH / 3) * Math.max(poreRoom, 0.25) + Math.max(0, e);
    pond = Math.max(0, pond - drain * h);
  }
  s.film = Math.max(0, film);
  s.pores = Math.min(P.poreCapMm, Math.max(0, pores));
  s.pond = pond;
  return s;
}

/** The normalised levels a material reads: film01 (gloss, drench), absorbed01 (darkening), pond01 (puddles). */
export interface WetLevels { film01: number; absorbed01: number; pond01: number }
export function wetLevels(s: WetState, cls: WetClass): WetLevels {
  const P = WET_PARAMS[cls];
  return {
    film01: P.filmCapMm > 0 ? Math.min(1, s.film / P.filmCapMm) : 0,
    absorbed01: P.poreCapMm > 0 ? Math.min(1, s.pores / P.poreCapMm) : 0,
    pond01: P.pondCapMm > 0 ? Math.min(1, s.pond / P.pondCapMm) : 0,
  };
}

// ── the weather → the clock's inputs ──

/** The weather's precipitation density 0..1 (drizzle ~0.3, steady ~0.6, storm 1) → rain rate (mm/h): 2.3 / 7 / 16. */
export function rainRateMmH(kind: string, density01: number): number {
  if (kind !== 'rain') return 0;
  const d = Math.max(0, Math.min(1, density01));
  return 16 * Math.pow(d, 1.6);
}
/** The weather's wind strength 0..1 → metres a second at 2 m (calm air still moves ~1 m/s; a gale is 12). */
export function windMsFrom(strength01: number): number { return 1 + 11 * Math.max(0, Math.min(1, strength01)); }
/** °C a surface cools below the air by radiating to a clear night sky (≈ 3 °C on a calm clear night, 0 under cloud or by day). */
export function nightCoolC(nightFactor01: number, cover01: number): number {
  return 3 * Math.max(0, Math.min(1, nightFactor01)) * (1 - Math.max(0, Math.min(1, cover01)));
}
/** Direct sun 0..1 from the night factor (0 day … 1 night) and the cloud cover. */
export function sunFrom(nightFactor01: number, cover01: number): number {
  return Math.max(0, 1 - nightFactor01) * Math.max(0, 1 - 0.85 * Math.max(0, Math.min(1, cover01)));
}

// ── the clock: one state per class, in the open ──

export interface WetClockInputs extends WetInputs {
  /** The resolver's wetness with the RAIN part taken out — a wetland's damp, dew on a humid biome: a floor the
   *  displayed ground never goes below (it drew exactly this before the clock, in dry weather). */
  floor01: number;
}

export class WetClock {
  readonly state: Record<WetClass, WetState> = {
    soil: newWetState(), stone: newWetState(), wood: newWetState(), fabric: newWetState(), metal: newWetState(), glass: newWetState(),
  };
  floor01 = 0;
  timeScale = WET_TIME_SCALE;
  /** real seconds stepped (diagnostics) */
  elapsedS = 0;
  lastInputs: WetClockInputs | null = null;

  step(inp: WetClockInputs, dtS: number): void {
    const dt = Math.min(1, Math.max(0, dtS)); // a hitch never dries the world in one step
    for (const c of WET_CLASSES) stepWet(this.state[c], c, inp, dt, this.timeScale);
    this.floor01 = Math.max(0, Math.min(1, inp.floor01));
    this.elapsedS += dt;
    this.lastInputs = inp;
  }

  levels(c: WetClass): WetLevels { return wetLevels(this.state[c], c); }

  /** The ground's three uniforms (lib/wetness.ts): darkening, gloss, puddles — never below the resolver's floor. */
  groundUniforms(): { absorbed: number; film: number; pond: number } {
    const l = this.levels('soil');
    const f = this.floor01;
    return { absorbed: Math.max(f, l.absorbed01), film: Math.max(f, l.film01), pond: Math.max(f, l.pond01, l.film01 * 0.6) };
  }

  /** Pin every class to a level (probes / beds): 0 = bone dry, 1 = soaked. */
  pin(level01: number): void {
    const k = Math.max(0, Math.min(1, level01));
    for (const c of WET_CLASSES) {
      const P = WET_PARAMS[c];
      this.state[c] = { film: P.filmCapMm * k, pores: P.poreCapMm * k, pond: P.pondCapMm * k };
    }
  }
}

/** The one clock of the session (WeatherModule steps it; the ground, the props and the puddle patch read it). */
export const wetClock = new WetClock();

/** How long (REAL seconds) a soaked surface of `cls` takes to fall below `level01` under constant inputs — the harness's and L's read of the rates. */
export function dryingTimeS(cls: WetClass, inp: WetInputs, level01 = 0.05, timeScale = WET_TIME_SCALE, maxS = 7200): number {
  const s: WetState = { film: WET_PARAMS[cls].filmCapMm, pores: WET_PARAMS[cls].poreCapMm, pond: WET_PARAMS[cls].pondCapMm };
  const dt = 0.5;
  for (let t = 0; t <= maxS; t += dt) {
    const l = wetLevels(s, cls);
    if (Math.max(l.film01, l.absorbed01) < level01) return t;
    stepWet(s, cls, inp, dt, timeScale);
  }
  return Infinity;
}

// ── which class a material is (pure: the name and the metalness) ──

/**
 * A material's wet class from what the asset tells us: the metals by metalness (a FINISHED atlas carries metal per
 * texel — the layer blends the metal class in by the fragment's own metalness, so it reads 'stone' here), glass /
 * fabric / wood by name, everything else porous stone (concrete, plaster, brick, render — most of the Commons).
 */
export function wetClassForMaterial(name: string, metalness: number, finished: boolean): WetClass {
  const n = (name || '').toLowerCase();
  if (/glass|window|\bpanes?\b|crystal|visor/.test(n)) return 'glass'; // never 'panel'
  if (/cloth|fabric|canvas|tent|awning|banner|flag|rug|carpet|curtain|sail|tarp|cushion|mattress|textile/.test(n)) return 'fabric';
  // painted metal is a sealed coat over steel: it beads a film and never soaks (the brief's "painted metal / glass")
  if (/painted|steel|iron|alloy|metal|chrome|\bhull/.test(n)) return 'metal';
  if (/wood|plank|timber|log|bark|crate|barrel|fence|bench|table|shingle/.test(n)) return 'wood';
  if (!finished && metalness >= 0.6) return 'metal';
  return 'stone';
}
